import React, { useState, useEffect, useRef } from "react";
import api from '../../api/api';
import { useAuth } from '../../context/AuthContext';
import ProfileModal from '../../Layout/Components/ProfileModal';

const AddRequestModal = ({ onClose, onAdd, requestData, respondMode, viewOnly, onRespond }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    title: requestData?.title || "",
    description: requestData?.description || "",
    status: requestData?.status || "در انتظار",
    people: requestData?.people || []
  });
  const [replyText, setReplyText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const myFlow = requestData?.flows?.find(
    (f) => f.status === 'pending' && (!user || Number(f.receiver) === Number(user.id))
  );
  const canRespond = respondMode && Boolean(myFlow);
  const isReadOnly = viewOnly || (respondMode && !myFlow);
  const responseFlows = requestData?.flows?.filter((flow) => flow.comment?.trim()) || [];
  const [isForwarding, setIsForwarding] = useState(false);
  const [forwardTo, setForwardTo] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [allPeople, setAllPeople] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewingProfile, setViewingProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const searchRef = useRef(null);

  const handleViewCreatorProfile = async () => {
    if (!requestData?.creator) return;
    setProfileLoading(true);
    try {
      const res = await api.get(`/users/${requestData.creator}/`);
      const u = res.data;
      setViewingProfile({
        ...u,
        avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(u.name || u.username)}&background=random&color=fff&size=128`
      });
    } catch (error) {
      console.error('خطا در دریافت پروفایل درخواست‌دهنده:', error);
    } finally {
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const res = await api.get('/users/');
        setAllPeople(
          res.data
            .filter(u => u.id !== user?.id) // خود کاربرِ درخواست‌دهنده نباید در لیست گیرندگان باشد
            .map(u => ({
              id: u.id,
              name: u.name || u.username,
              role: u.is_staff ? 'کارمند' : 'کاربر',
              avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(u.name || u.username)}&background=random&color=fff&size=128`
            }))
        );
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [user?.id]);

  const handleRespond = async (status, message) => {
    if (!myFlow) return;
    setSubmitting(true);
    try {
      await onRespond(myFlow.id, status, replyText);
    } finally {
      setSubmitting(false);
    }
  };

  const removePerson = (id) => {
    setFormData({ ...formData, people: formData.people.filter(p => p.id !== id) });
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      window.alert("لطفاً عنوان درخواست را وارد کنید.");
      return;
    }
    if (formData.people.length === 0) {
      window.alert("لطفاً حداقل یک گیرنده انتخاب کنید.");
      return;
    }

    const payload = {
      title: formData.title.trim(),
      description: formData.description.trim(),
      status: formData.status || "در انتظار",
      receiver: formData.people[0].id, // kept for backward compatibility
      receiver_ids: formData.people.map((p) => p.id), // every selected person gets their own approval flow
    };

    try {
      if (onAdd) {
        await onAdd(payload);
      } else {
        await api.post('/requests/', payload);
        window.alert("درخواست با موفقیت ثبت شد.");
      }
      onClose();
    } catch (error) {
      console.error('Error creating request:', error);
      const backendMsg = error.response?.data ? JSON.stringify(error.response.data) : "خطای نامشخص";
      window.alert("خطا در ثبت درخواست: " + backendMsg);
    }
  };

  const filteredPeople = allPeople.filter(p =>
    p.name.includes(searchQuery) && !formData.people.find(fp => fp.id === p.id)
  );

  const forwardCandidates = allPeople.filter(p =>
    p.name.includes(searchQuery) &&
    p.id !== requestData?.creator &&
    p.id !== user?.id
  );

  const handleForward = async () => {
    if (!myFlow) return;
    if (!forwardTo) {
      window.alert("لطفاً شخصی را برای ارجاع انتخاب کنید.");
      return;
    }
    setSubmitting(true);
    try {
      await api.patch(`/request-flows/${myFlow.id}/`, {
        status: 'forwarded',
        comment: replyText || ''
      });
      await api.post('/request-flows/', {
        request: requestData.id,
        receiver: forwardTo.id,
        status: 'pending',
        comment: ''
      });
      window.alert("درخواست با موفقیت ارجاع شد.");
      onClose();
    } catch (error) {
      console.error('خطا در ارجاع درخواست:', error);
      const backendMsg = error.response?.data ? JSON.stringify(error.response.data) : "خطای نامشخص";
      window.alert("خطا در ارجاع درخواست: " + backendMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-xs" onClick={onClose} />
      <div className="bg-white w-full max-w-[650px] rounded-3xl p-6 shadow-2xl relative z-10 border border-slate-100" dir="rtl">
        <h2 className="text-lg font-bold text-slate-800 mb-2">
          {respondMode ? "جزئیات درخواست" : requestData ? "ویرایش درخواست" : "ساخت درخواست جدید"}
        </h2>

        {requestData?.creator_name && (
          <button
            type="button"
            onClick={handleViewCreatorProfile}
            disabled={profileLoading}
            className="flex items-center gap-2 mb-4 p-2 pl-4 bg-slate-50 hover:bg-slate-100 border border-slate-100 rounded-2xl transition-colors disabled:opacity-60"
          >
            <img
              src={`https://ui-avatars.com/api/?name=${encodeURIComponent(requestData.creator_name)}&background=random&color=fff&size=128`}
              alt={requestData.creator_name}
              className="w-9 h-9 rounded-full object-cover"
            />
            <div className="text-right">
              <div className="text-[10px] text-slate-400">درخواست‌دهنده:</div>
              <div className="text-sm font-bold text-slate-800">{requestData.creator_name}</div>
            </div>
          </button>
        )}

        {loading && <div className="text-center py-2 text-sm text-slate-500">در حال بارگذاری کاربران...</div>}

        <form className="flex flex-col gap-5" onSubmit={(e) => { e.preventDefault(); if (!respondMode && !viewOnly) handleSave(); }}>
          {/* عنوان */}
          <div className="grid grid-cols-[80px_1fr] items-center gap-2">
            <label className="text-sm font-bold text-slate-800">عنوان:</label>
            <input
              value={formData.title}
              disabled={isReadOnly}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-indigo-500 disabled:bg-slate-100 disabled:text-slate-500"
            />
          </div>

          {/* توضیحات */}
          <div className="grid grid-cols-[80px_1fr] items-start gap-2">
            <label className="text-sm font-bold text-slate-800 mt-2">توضیحات:</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              disabled={isReadOnly}
              className="w-full px-4 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-indigo-500 disabled:bg-slate-100 disabled:text-slate-500"
              rows="3"
            />
          </div>

          {requestData && responseFlows.length > 0 && (
            <div className="border-t border-slate-100 pt-4">
              <h3 className="text-sm font-bold text-slate-800 mb-3">پاسخ‌ها و توضیحات</h3>
              <div className="space-y-2">
                {responseFlows.map((flow) => (
                  <div key={flow.id} className="rounded-xl bg-indigo-50 border border-indigo-100 p-3">
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <span className="text-xs font-bold text-indigo-800">
                        {flow.receiver_name || 'پاسخ‌دهنده'}
                      </span>
                      <span className="text-[10px] text-indigo-600">
                        {flow.status === 'approved' ? 'تایید شده' : flow.status === 'rejected' ? 'رد شده' : 'ارجاع شده'}
                      </span>
                    </div>
                    <p className="text-xs text-slate-700 whitespace-pre-wrap">{flow.comment}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* افزودن شخص — فقط موقع ساخت/ویرایش، نه موقع پاسخ‌دادن */}
          {!respondMode && !viewOnly && (
            <div className="grid grid-cols-[80px_1fr] items-start gap-2 relative" ref={searchRef}>
              <label className="text-sm font-bold text-slate-800 mt-2">گیرندگان:</label>
              <div className="w-full flex flex-col gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  {formData.people.map((person) => (
                    <div key={person.id} className="flex items-center gap-2 pr-1 pl-3 py-1 bg-indigo-50 border border-indigo-100 rounded-full">
                      <img src={person.avatar} alt={person.name} className="w-6 h-6 rounded-full object-cover" />
                      <span className="text-xs font-bold text-indigo-800">{person.name}</span>
                      <button type="button" onClick={() => removePerson(person.id)} className="text-indigo-400 hover:text-rose-500 font-bold">×</button>
                    </div>
                  ))}
                  <button type="button" onClick={() => setIsSearchOpen(!isSearchOpen)} className="w-8 h-8 rounded-full border border-dashed border-slate-300 text-slate-400 flex items-center justify-center hover:border-indigo-500 hover:text-indigo-500">+</button>
                </div>

                {isSearchOpen && (
                  <div className="absolute top-full right-0 w-full mt-1 bg-white border border-slate-200 shadow-2xl rounded-2xl z-50 max-h-60 overflow-y-auto">
                    <input autoFocus value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="جستجو..." className="w-full p-3 text-xs border-b border-slate-50 outline-none" />
                    {filteredPeople.length > 0 ? (
                      filteredPeople.map((user) => (
                        <div key={user.id} onClick={() => { setFormData({ ...formData, people: [...formData.people, user] }); setIsSearchOpen(false); }} className="px-4 py-3 hover:bg-slate-50 cursor-pointer flex items-center gap-3">
                          <img src={user.avatar} alt={user.name} className="w-8 h-8 rounded-full" />
                          <div>
                            <div className="font-bold text-sm">{user.name}</div>
                            <div className="text-[9px] text-slate-400">{user.role}</div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-xs text-slate-400 text-center">هیچ کاربری یافت نشد</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* بخش پاسخ‌دهی — فقط توی respondMode */}
          {canRespond && (
            <>
              <label className="flex items-center gap-2 text-sm font-bold text-slate-800 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isForwarding}
                  onChange={(e) => { setIsForwarding(e.target.checked); setForwardTo(null); setIsSearchOpen(false); }}
                />
                ارجاع به شخص دیگر
              </label>

              {isForwarding && (
                <div className="relative" ref={searchRef}>
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    {forwardTo && (
                      <div className="flex items-center gap-2 pr-1 pl-3 py-1 bg-indigo-50 border border-indigo-100 rounded-full">
                        <img src={forwardTo.avatar} alt={forwardTo.name} className="w-6 h-6 rounded-full" />
                        <span className="text-xs font-bold text-indigo-800">{forwardTo.name}</span>
                        <button type="button" onClick={() => setForwardTo(null)} className="text-indigo-400 hover:text-rose-500 font-bold">×</button>
                      </div>
                    )}
                    {!forwardTo && (
                      <button type="button" onClick={() => setIsSearchOpen(!isSearchOpen)} className="w-8 h-8 rounded-full border border-dashed border-slate-300 text-slate-400 flex items-center justify-center hover:border-indigo-500 hover:text-indigo-500">+</button>
                    )}
                  </div>

                  {isSearchOpen && !forwardTo && (
                    <div className="absolute top-full right-0 w-full mt-1 bg-white border border-slate-200 shadow-2xl rounded-2xl z-50 max-h-60 overflow-y-auto">
                      <input autoFocus value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="جستجو..." className="w-full p-3 text-xs border-b border-slate-50 outline-none" />
                      {forwardCandidates.length > 0 ? (
                        forwardCandidates.map((person) => (
                          <div key={person.id} onClick={() => { setForwardTo(person); setIsSearchOpen(false); }} className="px-4 py-3 hover:bg-slate-50 cursor-pointer flex items-center gap-3">
                            <img src={person.avatar} alt={person.name} className="w-8 h-8 rounded-full" />
                            <span className="text-sm">{person.name}</span>
                          </div>
                        ))
                      ) : (
                        <div className="p-4 text-xs text-slate-400 text-center">هیچ کاربری یافت نشد</div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="text-sm font-bold text-slate-800 block mb-2">
                  {isForwarding ? "توضیح ارجاع:" : "پاسخ شما:"}
                </label>
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="پاسخ یا توضیح خود را بنویسید..."
                  className="w-full px-4 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-indigo-500"
                  rows="3"
                />
              </div>

              {isForwarding ? (
                <button
                  type="button"
                  disabled={submitting || !forwardTo || !myFlow}
                  onClick={handleForward}
                  className="w-full py-3 bg-blue-600 text-white font-bold text-sm rounded-xl hover:bg-blue-700 disabled:opacity-50"
                >
                  ارسال ارجاع
                </button>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    disabled={submitting || !myFlow}
                    onClick={() => handleRespond('approved', 'درخواست با موفقیت تایید شد.')}
                    className="py-3 bg-green-600 text-white font-bold text-sm rounded-xl hover:bg-green-700 disabled:opacity-50"
                  >
                    تایید
                  </button>
                  <button
                    type="button"
                    disabled={submitting || !myFlow}
                    onClick={() => handleRespond('rejected', 'درخواست رد شد.')}
                    className="py-3 bg-rose-600 text-white font-bold text-sm rounded-xl hover:bg-rose-700 disabled:opacity-50"
                  >
                    رد
                  </button>
                </div>
              )}

            </>
          )}

          {!respondMode && (
            <button type="button" onClick={handleSave} className="w-full py-3 bg-indigo-600 text-white font-bold text-sm rounded-xl hover:bg-indigo-700 transition-all mt-4">
              ذخیره تغییرات
            </button>
          )}
        </form>
      </div>

      {viewingProfile && (
        <ProfileModal
          user={viewingProfile}
          isOpen={!!viewingProfile}
          onClose={() => setViewingProfile(null)}
          canEdit={false}
        />
      )}
    </div>
  );
};

export default AddRequestModal;
